import { useCurrentApplication, useFormatMessage } from 'app/common/hooks/';
import { get, isEmpty } from 'lodash';
import messages from '../ItemAvailability.messages';
import { useContentInfo, useFetchContent } from 'app/content/hooks';
import { useContext, useEffect, useMemo, useState } from 'react';
import * as RequestStatus from 'app/common/hooks/useRestApi/RequestStatus.js';
import ContentModelContext, {
  addContentModel,
  generateContentKey,
  getContentModel,
  useGetContentModelKeyGens
} from 'app/content/contexts/ContentModelContext.js';

const useInventoryMessaging = () => {
  const { inventoryMessagingModel: modelName } = useContentInfo();
  const fetchRestApiState = useFetchContent({
    modelName,
    sendImmediately: false
  });

  useExecuteContentRequest({ fetchRestApiState, modelName });
};

/**
 * Performs API request to fetch inventory messaging content. Sets the content in the ContentModelContext.
 * @param fetchRestApiState {Object} - The state of the fetch request, including error, response, requestStatus, and sendCallback.
 * @param modelName {string} - The name of the content model to fetch.
 */
function useExecuteContentRequest({ fetchRestApiState, modelName }) {
  const { error, exception, response, requestStatus, sendCallback } =
    fetchRestApiState;
  const [inventoryMessages, setInventoryMessages] = useState({});
  const [done, setDone] = useState(false);
  const contentModelContext = useContext(ContentModelContext);
  const contentModelKeyGens = useGetContentModelKeyGens();
  const fallbackMessages = useFallbackMessages();
  const contentKey = getModelKeyGen(contentModelKeyGens);

  useEffect(() => {
    if (
      requestStatus === RequestStatus.IN_PROGRESS ||
      isValid(inventoryMessages) ||
      done ||
      isEmpty(contentModelKeyGens)
    ) {
      return;
    } else if (requestStatus === RequestStatus.COMPLETE) {
      if (error || get(response, 'results', []).length === 0) {
        setInventoryMessages(fallbackMessages);
      } else {
        setInventoryMessages(response.results[0]);
      }
    } else if (requestStatus === RequestStatus.PENDING) {
      const existing = getContentModel({
        name: modelName,
        keyGen: contentKey,
        contentModelContext
      });
      if (existing && isValid(existing)) {
        setInventoryMessages(existing);
      } else {
        sendCallback();
      }
    }
  }, [
    response,
    requestStatus,
    error,
    exception,
    inventoryMessages,
    modelName,
    sendCallback,
    contentModelContext,
    contentKey,
    contentModelKeyGens,
    done,
    fallbackMessages
  ]);

  useEffect(() => {
    if (done || !isValid(inventoryMessages)) {
      return;
    }
    addContentModel({
      name: modelName,
      content: inventoryMessages,
      keyGen: contentKey,
      contentModelContext
    });
    setDone(true);
  }, [inventoryMessages, modelName, contentKey, contentModelContext, done]);
}

function isValid(content) {
  return (
    !isEmpty(content) &&
    !isBlank(get(content, 'fields.inStockLabel')) &&
    !isBlank(get(content, 'fields.outOfStockLabel'))
  );
}

function isBlank(str) {
  return str === '';
}

function useFallbackMessages() {
  const formatMessage = useFormatMessage();
  return useMemo(() => {
    return {
      fields: {
        inStockLabel: {
          name: 'inStockLabel',
          value: formatMessage(messages.availability, {
            isAvailable: true
          })
        },
        outOfStockLabel: {
          name: 'outOfStockLabel',
          value: formatMessage(messages.availability, {
            isAvailable: false
          })
        }
      }
    };
  }, [formatMessage]);
}

const useGetInventoryMessageForProduct = product => {
  const contentModelKeyGens = useGetContentModelKeyGens();
  const contentModelContext = useContext(ContentModelContext);
  const { inventoryMessagingModel: name } = useContentInfo();
  const fallbackValue = useFallbackMessages();
  const { identifierValue, preferredPdc } = useCurrentApplication();

  const keyGen = getModelKeyGen(contentModelKeyGens);
  const [inventoryMessagesContent, setInventoryMessagesContent] = useState();

  const available = get(product, 'inventorySummary.available', false);
  const hasQuantity = get(product, 'inventoryQty', 0) > 0;
  const isAvailable = available || hasQuantity;
  const locationQtys = get(product, 'inventorySummary.locationQuantities', {});

  const contentKey = useMemo(() => {
    const key = generateContentKey(name, keyGen);
    if (isPreview(keyGen)) {
      return undefined;
    }
    return key;
  }, [name, keyGen]);

  const maybeContent = contentModelContext[contentKey];
  const hasValidContent = useMemo(() => {
    // useGetInventoryMessageForProduct doesnt actually call out to the api,
    // we need to have a dependency on the value of the content map to ensure it updates after content is loaded.
    return isValid(maybeContent);
  }, [maybeContent]);

  useEffect(() => {
    if (!hasValidContent) {
      setInventoryMessagesContent(fallbackValue);
      return;
    }
    if (!contentModelContext || isEmpty(contentModelKeyGens)) {
      return;
    }
    // Get the content from context. Does not call API.
    const content = getContentModel({
      name,
      keyGen,
      contentModelContext,
      fallbackKeys: getFallbackKeys(contentModelKeyGens),
      fallbackValue
    });
    setInventoryMessagesContent(content);
  }, [
    name,
    keyGen,
    contentModelContext,
    contentModelKeyGens,
    fallbackValue,
    hasValidContent
  ]);

  return useMemo(() => {
    if (inventoryMessagesContent) {
      return getInventoryMessage(
        inventoryMessagesContent,
        isAvailable,
        locationQtys,
        identifierValue,
        preferredPdc,
        fallbackValue
      );
    }
    return undefined;
  }, [
    inventoryMessagesContent,
    isAvailable,
    locationQtys,
    identifierValue,
    preferredPdc,
    fallbackValue
  ]);
};

function getFallbackKeys(contentModelKeyGens) {
  const fallbackKeys = [];
  if (contentModelKeyGens.dealerNetwork) {
    fallbackKeys.push(contentModelKeyGens.dealerNetwork);
  }
  if (contentModelKeyGens.tenant) {
    fallbackKeys.push(contentModelKeyGens.tenant);
  }
  return fallbackKeys;
}

function getModelKeyGen(contentModelKeyGens) {
  if (contentModelKeyGens.currentStore) {
    return contentModelKeyGens.currentStore;
  } else if (contentModelKeyGens.dealerNetwork) {
    return contentModelKeyGens.dealerNetwork;
  } else {
    return contentModelKeyGens.tenant;
  }
}

function getInventoryMessage(
  content,
  isAvailable,
  locationQtys,
  identifierValue,
  preferredPdc,
  fallbackValue
) {
  if (isAvailable) {
    return get(
      content,
      'fields.inStockLabel.value',
      get(fallbackValue, 'fields.inStockLabel.value')
    );
  } else {
    const outOfStockFallback = get(
      fallbackValue,
      'fields.outOfStockLabel.value'
    );
    // Check for any location keys starting with "D-" and not matching identifierValue
    const hasNetworkStock = Object.keys(locationQtys || {}).some(
      key =>
        key.startsWith('D-') && key !== identifierValue && locationQtys[key] > 0
    );
    if (hasNetworkStock) {
      return get(
        content,
        'fields.networkInStockLabel.value',
        outOfStockFallback
      );
    }

    // If no network stock, check for pdc stock
    if (get(locationQtys, preferredPdc, 0) > 0) {
      return get(content, 'fields.pdcInStockLabel.value', outOfStockFallback);
    }

    return get(content, 'fields.outOfStockLabel.value', outOfStockFallback);
  }
}

function isPreview(keyGen) {
  return get(keyGen, 'isPreview', false);
}

export default useInventoryMessaging;
export { useGetInventoryMessageForProduct };
